import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { QueryError } from '@/components/ui/QueryError';
import { Tabs, tabPanelId } from '@/components/ui/Tabs';
import type { Option } from '@/constants/institute';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import type { ClassSection, InstituteClass } from '@/types/instituteClass';

import { classesApi } from '../api/classes.api';
import { AcademicStructureTab } from '../components/setup/AcademicStructureTab';
import { AssignClassTeacherModal } from '../components/setup/AssignClassTeacherModal';
import { SectionMonitorModal } from '../components/setup/SectionMonitorModal';
import { SubjectTeachersModal } from '../components/setup/SubjectTeachersModal';
import {
  ClassesFilterBar,
  EMPTY_CLASS_FILTERS,
  isNarrowed,
  SORT_OPTIONS,
  type ClassFilters,
} from '../components/setup/ClassesFilterBar';
import { ClassesInsightRow } from '../components/ClassesInsightRow';
import { ClassesOverviewTable, ROWS_PER_PAGE } from '../components/setup/ClassesOverviewTable';
import { ClassesSetupHeader } from '../components/setup/ClassesSetupHeader';
import {
  ClassesStatRow,
  type ClassesTotals,
  type StructureFocus,
} from '../components/setup/ClassesStatRow';
import { DuplicateClassModal } from '../components/setup/DuplicateClassModal';
import { HowItWorksModal } from '../components/setup/HowItWorksModal';
import { SuspendedClassesList } from '../components/setup/SuspendedClassesList';
import { classSchema } from '../schemas/class.schema';
import { CLASSES_PALETTE } from '../theme';
import {
  CLASS_STAGES,
  classNameOptions,
  schoolMediumOptions,
  sortByTeachingOrder,
  stageOfClass,
  toFormValues,
  toSectionRows,
  type ClassSectionRow,
} from '../utils';

const SETUP_TABS = ['overview', 'structure', 'suspended'] as const;
type SetupTab = (typeof SETUP_TABS)[number];

/**
 * The screen's filters, as they sit in the address bar: the filter bar's own,
 * the page, rows per page and the open tab. Rows per page travels with the
 * page number: a page restored at a different size would show other rows.
 */
type Filters = Pick<ClassFilters, keyof ClassFilters> & {
  page: number;
  limit: number;
  tab: SetupTab;
};

const DEFAULT_FILTERS: Filters = {
  ...EMPTY_CLASS_FILTERS,
  page: 1,
  limit: ROWS_PER_PAGE[0],
  tab: 'overview',
};

/**
 * What a link may set. Grade, teacher and medium come from the roster and are
 * not listed — an unknown one only empties the table, which Clear undoes.
 */
const FILTER_OPTIONS = {
  allowed: {
    stage: ['ALL', ...CLASS_STAGES],
    sort: SORT_OPTIONS.map((option) => option.value),
    limit: ROWS_PER_PAGE,
    tab: SETUP_TABS,
  },
};

/** The five headline figures, counted across the whole roster rather than the filtered view. */
function totalsOf(rows: InstituteClass[]): ClassesTotals {
  return {
    classes: rows.length,
    sections: rows.reduce((sum, row) => sum + row.sections.length, 0),
    students: rows.reduce((sum, row) => sum + row.studentCount, 0),
    // Counted by person, not by allotment: one teacher running 6-A and 7-B is
    // one teacher, and summing the columns would say two.
    teachers: new Set(rows.flatMap((row) => row.classTeachers.map((teacher) => teacher.uuid))).size,
    subjects: new Set(
      rows.flatMap((row) => [...row.coreSubjects, ...row.electives, ...row.languages]),
    ).size,
  };
}

/**
 * Whether one section's row belongs in the view.
 *
 * The class's own filters (year, grade, stage, medium) apply to every
 * section of it. The teacher filter and a teacher's name in the search apply to
 * the section that teacher runs, so choosing 9-B's class teacher shows 9-B, not
 * 9-A beside her.
 */
function matches(row: ClassSectionRow, filters: ClassFilters): boolean {
  const { instituteClass: klass, section, classTeacher } = row;
  if (filters.academicYear && klass.academicYear.name !== filters.academicYear) return false;
  if (filters.grade !== 'ALL' && klass.uuid !== filters.grade) return false;
  if (filters.stage !== 'ALL' && stageOfClass(klass) !== filters.stage) return false;
  if (filters.medium !== 'ALL' && !klass.effectiveMediums.includes(filters.medium)) return false;
  if (filters.teacher !== 'ALL' && classTeacher?.uuid !== filters.teacher) return false;

  const needle = filters.search.trim().toLowerCase();
  if (!needle) return true;

  return (
    klass.name.toLowerCase().includes(needle) ||
    (klass.code?.toLowerCase().includes(needle) ?? false) ||
    // "B" finds every B section; "9th - B" or "9th-B" finds the one.
    (section !== null &&
      (section.name.toLowerCase().includes(needle) ||
        `${klass.name} - ${section.name}`.toLowerCase().includes(needle) ||
        `${klass.name}-${section.name}`.toLowerCase().includes(needle))) ||
    [...klass.coreSubjects, ...klass.electives, ...klass.languages].some((subject) =>
      subject.toLowerCase().includes(needle),
    ) ||
    (classTeacher?.fullName.toLowerCase().includes(needle) ?? false)
  );
}

/**
 * Setting up the classes an institute runs, and the sections inside each one.
 *
 * Read-only for anyone holding `CLASS_VIEW` without `CLASS_MANAGE`. The server
 * enforces the same split; this only decides what is worth offering.
 *
 * Filtering, sorting and paging are done in the browser on purpose: the roster
 * is capped at 200 classes server-side and no institute runs a tenth of that,
 * so a round trip per keystroke would buy nothing. `ClassFilters` is one object
 * so that the day it does need to go to the server, it goes as one.
 */
export function ClassesPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('CLASS_MANAGE'));
  // Who runs a section is the staff room's arrangement, and its own key: an
  // institute may let somebody set classes up without letting them move a
  // teacher off a register.
  const canAllotTeachers = Boolean(permissions?.includes('ALLOCATION_MANAGE'));
  const canViewAllocations = Boolean(permissions?.includes('ALLOCATION_VIEW'));

  const { data, isLoading, error, refetch } = useApiQuery(() => classesApi.list(), []);
  // The school's class range and mediums, which the Duplicate dialog's names
  // and the medium filter follow. Not essential: if it fails, both offer the
  // full list.
  const { data: setupOptions } = useApiQuery(() => classesApi.setupOptions(), []);
  const rows = useMemo(() => data ?? [], [data]);

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { tab, page, limit } = filters;
  /** Which structure panel a figure asked for, or null when the tab was clicked. */
  const [structureFocus, setStructureFocus] = useState<StructureFocus | null>(null);
  const [deleting, setDeleting] = useState<InstituteClass | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  /** The class a copy is being written for, so its menu entry can stand down. */
  const [duplicating, setDuplicating] = useState<string | null>(null);
  /** The class awaiting a name before it is copied. */
  const [confirmingDuplicate, setConfirmingDuplicate] = useState<InstituteClass | null>(null);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  /** Bumped when a class is suspended, so the Suspended view reads it at once. */
  const [suspendedVersion, setSuspendedVersion] = useState(0);
  // Read here rather than inside the Suspended view, so its tab can carry a
  // count before anybody opens it.
  const suspendedClasses = useApiQuery(() => classesApi.listSuspended(), [suspendedVersion]);
  const suspendedSections = useApiQuery(() => classesApi.listSuspendedSections(), [suspendedVersion]);
  /**
   * Why the server refused to suspend, shown in the dialog itself — a toast
   * raised as the dialog closed told the office something it then had to
   * remember while it went to move the children.
   */
  const [suspendRefusal, setSuspendRefusal] = useState<string | null>(null);
  /** One section on its way out, with its class for the wording. */
  const [removingSection, setRemovingSection] = useState<{
    instituteClass: InstituteClass;
    section: ClassSection;
  } | null>(null);
  const [isRemovingSection, setIsRemovingSection] = useState(false);
  const [sectionRefusal, setSectionRefusal] = useState<string | null>(null);
  /** The class whose section teachers are being arranged, in a modal on this screen. */
  const [allotting, setAllotting] = useState<InstituteClass | null>(null);
  const [viewingTeachers, setViewingTeachers] = useState<InstituteClass | null>(null);
  const [choosingMonitor, setChoosingMonitor] = useState<{ uuid: string; label: string } | null>(null);

  const totals = useMemo(() => totalsOf(rows), [rows]);
  /** What a copy may be called — the server refuses a name already in use. */
  const classNames = useMemo(
    () => classNameOptions(undefined, setupOptions?.classRange),
    [setupOptions],
  );
  /** One row per section — see `ClassSectionRow` for why. */
  const filtered = useMemo(
    () =>
      sortByTeachingOrder(rows, filters.sort)
        .flatMap(toSectionRows)
        .filter((row) => matches(row, filters)),
    [rows, filters],
  );
  /** Classes in view, for the filter bar's count — a class, not a section, is what it names. */
  const matchingClasses = useMemo(
    () => new Set(filtered.map((row) => row.instituteClass.uuid)).size,
    [filtered],
  );

  /** The sessions the roster mentions. The endpoint answers for one today. */
  const yearOptions: Option[] = useMemo(() => {
    const names = [...new Set(rows.map((row) => row.academicYear.name))];
    return names.map((name) => ({ value: name, label: name }));
  }, [rows]);

  const gradeOptions: Option[] = useMemo(
    () => rows.map((row) => ({ value: row.uuid, label: row.name })),
    [rows],
  );

  /**
   * The school's own mediums, plus any a class on the roster is taught in, so
   * no class can sit behind a medium the filter does not offer.
   */
  const mediumOptions: Option[] = useMemo(
    () =>
      schoolMediumOptions([
        ...new Set([...(setupOptions?.institute.mediums ?? []), ...rows.flatMap((row) => row.effectiveMediums)]),
      ]),
    [setupOptions, rows],
  );

  /** One entry per teacher, however many sections they run. */
  const teacherOptions: Option[] = useMemo(() => {
    const byUuid = new Map<string, string>();
    rows.forEach((row) =>
      row.classTeachers.forEach((teacher) => byUuid.set(teacher.uuid, teacher.fullName)),
    );

    return [...byUuid.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const totalPages = Math.max(Math.ceil(filtered.length / limit), 1);
  // A filter can shorten the list under the reader's feet; landing on page 4 of
  // 2 would show an empty table rather than the rows that are left.
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * limit, currentPage * limit);

  /** Every filter change resets to the first page — page 3 of the old list means nothing in the new one. */
  function changeFilters(patch: Partial<ClassFilters>) {
    setFilters({ ...patch, page: 1 });
  }

  function clearFilters() {
    setFilters({ ...EMPTY_CLASS_FILTERS, page: 1 });
  }

  function setPage(next: number) {
    setFilters({ page: next });
  }

  /**
   * Opens the academic structure, optionally on the panel that answers the
   * figure the reader clicked.
   *
   * The focus is cleared first even when it has not changed, so clicking the
   * same figure twice scrolls again rather than doing nothing the second time —
   * the effect that scrolls only fires when the value moves.
   */
  function openStructure(focus: StructureFocus | null = null) {
    setFilters({ tab: 'structure' });
    setStructureFocus(null);
    if (focus) window.requestAnimationFrame(() => setStructureFocus(focus));
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** What went wrong, in the server's words where it gave any. */
  function reasonFor(caught: unknown): string {
    return caught instanceof ApiRequestError ? caught.message : 'Please try again.';
  }

  /**
   * A second class with the same sections, subjects and fee.
   *
   * Built by running the row back through the form's own schema rather than by
   * hand-assembling a payload — the schema is what decides what a valid class
   * is, and a second opinion here would drift from it. The code is dropped
   * because it is unique per institute; the name is one the office picked from
   * those still free.
   */
  async function duplicateClass(row: InstituteClass, name: string) {
    const parsed = classSchema.safeParse({ ...toFormValues(row), name, code: '' });
    if (!parsed.success) {
      toast.error(`Could not duplicate ${row.name}`, 'Open the class and check its setup first.');
      return;
    }

    setDuplicating(row.uuid);
    try {
      await classesApi.create(parsed.data);
      toast.success(`${name} added`, 'Same sections, subjects and fee. Nobody is admitted into it yet.');
      refetch();
    } catch (caught) {
      toast.error(`Could not duplicate ${row.name}`, reasonFor(caught));
    } finally {
      setDuplicating(null);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    const removed = deleting;
    setIsDeleting(true);

    try {
      await classesApi.remove(removed.uuid);
      setDeleting(null);
      toast.success(
        `${removed.name} has been suspended`,
        'It is no longer offered on admissions. Reactivate it from the Suspended tab.',
      );
      setSuspendedVersion((version) => version + 1);
      refetch();
    } catch (caught) {
      // Kept open, with the reason inside it, so the office reads what to do
      // beside the class it was about.
      setSuspendRefusal(reasonFor(caught));
    } finally {
      setIsDeleting(false);
    }
  }

  async function confirmRemoveSection() {
    if (!removingSection) return;
    const { instituteClass, section } = removingSection;
    setIsRemovingSection(true);

    try {
      await classesApi.removeSection(section.uuid);
      setRemovingSection(null);
      toast.success(
        `${instituteClass.name} - ${section.name} has been suspended`,
        `${instituteClass.name}'s other sections are unchanged. Reactivate it from the Suspended tab.`,
      );
      setSuspendedVersion((version) => version + 1);
      refetch();
    } catch (caught) {
      // Same as suspending a class: the reason stays beside the section it is about.
      setSectionRefusal(reasonFor(caught));
    } finally {
      setIsRemovingSection(false);
    }
  }

  return (
    // `CLASSES_PALETTE` re-points `--accent` and friends to the console's blue
    // for this subtree, so every token-drawn control here — buttons, tabs, icon
    // tiles, focus rings — takes the institute console's colour instead of the
    // product's default teal, without a single component being forked.
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CLASSES_PALETTE)}>
      <ClassesSetupHeader
        canManage={canManage}
        onAddClass={() => navigate('/admin/classes/new')}
        onHowItWorks={() => setIsHowItWorksOpen(true)}
      />

      {error ? (
        <QueryError title="Could not load your classes" error={error} onRetry={refetch} />
      ) : (
        <>
          <ClassesStatRow
            totals={totals}
            isLoading={isLoading}
            isShowingAll={tab === 'overview' && !isNarrowed(filters)}
            // One write for both: two in one event would each start from the old address.
            onShowAllClasses={() => setFilters({ ...EMPTY_CLASS_FILTERS, page: 1, tab: 'overview' })}
            onOpenStructure={openStructure}
          />

          <Tabs
            label="Class views"
            value={tab}
            onChange={(next) => {
              setFilters({ tab: next });
              setStructureFocus(null);
            }}
            options={[
              // The whole roster, like the stat row above — not the filtered view.
              { value: 'overview', label: 'Classes overview', badge: isLoading ? undefined : totals.classes },
              { value: 'structure', label: 'Academic structure' },
              {
                value: 'suspended',
                label: 'Suspended',
                // Entries the tab lists: whole classes, plus sections suspended on their own.
                badge:
                  suspendedClasses.data && suspendedSections.data
                    ? suspendedClasses.data.length + suspendedSections.data.length
                    : undefined,
              },
            ]}
          />

          {tab === 'overview' ? (
            <div
              id={tabPanelId('overview')}
              role="tabpanel"
              aria-labelledby="overview-tab"
              className="flex flex-col gap-6 animate-tab-fade"
            >
              <ClassesFilterBar
                filters={filters}
                onChange={changeFilters}
                onClear={clearFilters}
                yearOptions={yearOptions}
                gradeOptions={gradeOptions}
                teacherOptions={teacherOptions}
                mediumOptions={mediumOptions}
                resultCount={matchingClasses}
                isLoading={isLoading}
              />

              <ClassesOverviewTable
                rows={paginated}
                meta={{ page: currentPage, limit, total: filtered.length, totalPages }}
                canManage={canManage}
                canAllotTeachers={canAllotTeachers}
                canViewAllocations={canViewAllocations}
                isLoading={isLoading}
                isNarrowed={isNarrowed(filters)}
                busyUuid={duplicating}
                onPageChange={setPage}
                onLimitChange={(next) => setFilters({ limit: next, page: 1 })}
                onClearFilters={clearFilters}
                onAddClass={() => navigate('/admin/classes/new')}
                onEdit={(row) => navigate(`/admin/classes/${row.uuid}/edit`)}
                onDelete={setDeleting}
                onDeleteSection={(instituteClass, section) => setRemovingSection({ instituteClass, section })}
                onDuplicate={setConfirmingDuplicate}
                onAssignTeacher={setAllotting}
                onViewSubjectTeachers={setViewingTeachers}
                onSetMonitor={
                  canManage
                    ? (row, section) => setChoosingMonitor({ uuid: section.uuid, label: `${row.name} - ${section.name}` })
                    : undefined
                }
              />
            </div>
          ) : tab === 'suspended' ? (
            <div
              id={tabPanelId('suspended')}
              role="tabpanel"
              aria-labelledby="suspended-tab"
              className="animate-tab-fade"
            >
              <SuspendedClassesList
                canManage={canManage}
                classes={suspendedClasses}
                sections={suspendedSections}
                onReinstated={refetch}
              />
            </div>
          ) : (
            <div
              id={tabPanelId('structure')}
              role="tabpanel"
              aria-labelledby="structure-tab"
              className="animate-tab-fade"
            >
              <AcademicStructureTab rows={rows} isLoading={isLoading} focus={structureFocus} />
            </div>
          )}

          <div className="pt-2">
            <ClassesInsightRow
              rows={rows}
              isLoading={isLoading}
              onViewReport={openStructure}
              onViewStructure={openStructure}
              onAddClass={() => navigate('/admin/classes/new')}
            />
          </div>
        </>
      )}

      {/* Mounted only once a class is chosen, so each open asks the server
          fresh rather than drawing last time's teachers. */}
      {allotting && (
        <AssignClassTeacherModal
          instituteClass={allotting}
          isOpen
          onClose={() => setAllotting(null)}
          onSaved={refetch}
        />
      )}

      {choosingMonitor && (
        <SectionMonitorModal
          sectionUuid={choosingMonitor.uuid}
          label={choosingMonitor.label}
          onClose={() => setChoosingMonitor(null)}
          onSaved={refetch}
        />
      )}

      {viewingTeachers && (
        <SubjectTeachersModal
          instituteClass={viewingTeachers}
          isOpen
          onClose={() => setViewingTeachers(null)}
          onSaved={refetch}
          canEdit={canAllotTeachers}
          onAssignClassTeacher={
            canAllotTeachers
              ? () => {
                  setAllotting(viewingTeachers);
                  setViewingTeachers(null);
                }
              : undefined
          }
        />
      )}

      <HowItWorksModal
        isOpen={isHowItWorksOpen}
        onClose={() => setIsHowItWorksOpen(false)}
        onAddClass={canManage ? () => navigate('/admin/classes/new') : undefined}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        title={`Suspend ${deleting?.name ?? 'this class'}?`}
        description="Nothing is deleted. The class stops being offered on admission forms and fee plans, and its record is kept so past years stay readable."
        confirmLabel="Suspend class"
        tone="danger"
        isLoading={isDeleting}
        // Said before the click where the roster already knows it; the server
        // has the last word (it also counts inactive and suspended children),
        // and its answer lands in the same place.
        isConfirmDisabled={Boolean(deleting && deleting.studentCount > 0) || suspendRefusal !== null}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          setDeleting(null);
          setSuspendRefusal(null);
        }}
      >
        {deleting && (deleting.studentCount > 0 || suspendRefusal) && (
          <Alert tone="warning" title="This class cannot be suspended yet">
            {suspendRefusal ??
              `${deleting.name} still has ${deleting.studentCount} ${
                deleting.studentCount === 1 ? 'student' : 'students'
              } on the roll. Move them to another class, or mark them transferred or alumni, before suspending it.`}
          </Alert>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={Boolean(removingSection)}
        title={
          removingSection
            ? `Suspend ${removingSection.instituteClass.name} - ${removingSection.section.name}?`
            : 'Suspend this section?'
        }
        description={
          removingSection
            ? `Only this section is suspended. ${removingSection.instituteClass.name}'s other sections, subjects and fee stay as they are, its past records are kept, and you can reactivate it from the Suspended tab.`
            : undefined
        }
        confirmLabel="Suspend section"
        tone="danger"
        isLoading={isRemovingSection}
        isConfirmDisabled={sectionRefusal !== null}
        onConfirm={() => void confirmRemoveSection()}
        onCancel={() => {
          setRemovingSection(null);
          setSectionRefusal(null);
        }}
      >
        {sectionRefusal && (
          <Alert tone="warning" title="This section cannot be suspended yet">
            {sectionRefusal}
          </Alert>
        )}
      </ConfirmDialog>

      <DuplicateClassModal
        source={confirmingDuplicate}
        nameOptions={classNames}
        isLoading={Boolean(duplicating)}
        onConfirm={(name) => {
          const row = confirmingDuplicate;
          setConfirmingDuplicate(null);
          if (row) void duplicateClass(row, name);
        }}
        onCancel={() => setConfirmingDuplicate(null)}
      />
    </div>
  );
}
